import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { VCardService } from '../services/vcardService';
import { useTheme } from '../contexts/ThemeContext';
import { useContent } from '../hooks/useContent';
import { CONFIG } from '../utils/config';
import VCardEditModal from '../components/VCardEdit';

const VCardManager = () => {
	const { isDark } = useTheme();
	const { content, loading: contentLoading } = useContent('vCardManagerPage'); // <-- new i18n key

	const [vcards, setVcards] = useState([]);
	const [loading, setLoading] = useState(true);
	const [modal, setModal] = useState(false);
	const [form, setForm] = useState({ name: '', phone: '', mail: '', company: '' });
	const [editingId, setEditingId] = useState(null);

	const fetchVcards = async () => {
		try {
			const { vcards } = await VCardService.list();
			setVcards(vcards);
		} catch (e) {
			console.error(e);
			alert(e.message || 'Erreur lors du chargement');
		} finally {
			setLoading(false);
		}
	};
	useEffect(() => { fetchVcards(); }, []);

	const resetForm = () => {
		setForm({ name: '', phone: '', mail: '', company: '' });
		setEditingId(null);
	};
	const openCreate = () => {
		resetForm();
		setModal(true);
	};
	const openEdit = (vc) => {
		setForm(vc.data);
		setEditingId(vc.uuid);
		setModal(true);
	};
	const save = async () => {
		try {
			if (editingId) {
				await VCardService.update(editingId, form);
			} else {
				await VCardService.create(form);
			}
			setModal(false);
			fetchVcards();
		} catch (e) {
			alert(e.message || 'Erreur lors de la sauvegarde');
		}
	};
	const remove = async (uuid) => {
		if (!window.confirm('Supprimer ce vCard ?')) return;
		try {
			await VCardService.remove(uuid);
			fetchVcards();
		} catch (e) {
			alert(e.message || 'Erreur lors de la suppression');
		}
	};

	const short = (uuid) => uuid.slice(0, 6);

	if (contentLoading || !content || loading)
		return (
			<div className={`pt-16 min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
			<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
			</div>
		);

const label = (key) => content[key + 'Label'] ?? key;

	return (
		<div className={`min-h-screen ${isDark ? '' : 'bg-gray-50'}`}>
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
		{/* header block */}
		<div className="flex items-center justify-between mb-6 md:mb-8">
		<div className="flex items-center">
		<Link
		to="/dashboard"
		className={`mr-3 md:mr-4 p-2 transition-colors duration-200 ${
			isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
		}`}
		>
		<svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
		<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
		</svg>
		</Link>
		<div>
		<h1 className={`text-xl md:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
		{content.pageTitle}
		</h1>
		<p className={`text-sm md:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
		{content.pageSubtitle}
		</p>
		</div>
		</div>
		<button
		onClick={openCreate}
		className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600"
		>
		{content.createCta}
		</button>
		</div>

		{!vcards.length ? (
			<div className={`text-center py-12 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
			<p>{content.empty}</p>
			</div>
		) : (
			<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
			{vcards.map(({ data: { data: v, uuid }, number }) => {
				// helper : n’affiche que si valeur non vide
				const show = (label, val) =>
					val ? <p className="text-sm"><span className="font-medium">{label}</span>{val}</p> : null;

				return (
					<div
					key={uuid}
					className={`rounded-2xl border p-6 flex flex-col ${
						isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-gray-200 shadow-sm'
					}`}
					>
					{/* 1. name / company */}
					<div className="h-14">
					<h3 className={`text-xl font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
					#{number} – {v.name || 'Sans nom'}
					</h3>
					{show(content.companyLabel, v.company)}
					</div>

					{/* 2. tous les champs */}
					<div className={`mt-2 text-sm space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					{v.phone        && <p><span className="font-medium">{content.cardDisplay.phone}: </span>{v.phone}</p>}
					{v.phone_work   && <p><span className="font-medium">{content.cardDisplay.phoneWork}: </span>{v.phone_work}</p>}
					{v.phone_mobile && <p><span className="font-medium">{content.cardDisplay.phoneMobile}: </span>{v.phone_mobile}</p>}
					{v.mail         && <p><span className="font-medium">{content.cardDisplay.mail}: </span>{v.mail}</p>}
					{v.job_title    && <p><span className="font-medium">{content.cardDisplay.jobTitle}: </span>{v.job_title}</p>}
					{v.street       && <p><span className="font-medium">{content.cardDisplay.street}: </span>{v.street}</p>}
					{v.city         && <p><span className="font-medium">{content.cardDisplay.city}: </span>{v.city}</p>}
					{v.state        && <p><span className="font-medium">{content.cardDisplay.state}: </span>{v.state}</p>}
					{v.zipcode      && <p><span className="font-medium">{content.cardDisplay.zipcode}: </span>{v.zipcode}</p>}
					{v.country      && <p><span className="font-medium">{content.cardDisplay.country}: </span>{v.country}</p>}
					{v.website      && <p><span className="font-medium">{content.cardDisplay.website}: </span>{v.website}</p>}
					{v.birthday     && <p><span className="font-medium">{content.cardDisplay.birthday}: </span>{v.birthday}</p>}
					{v.personal_note&& <p><span className="font-medium">{content.cardDisplay.personalNote}: </span>{v.personal_note}</p>}
					</div>

					{/* 3. spacer */}
					<div className="grow" />

					{/* 4. boutons */}
					<div className="flex items-center justify-end gap-2 pt-4">
					<button
					onClick={() => openEdit({ data: v, uuid })}
					className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
						isDark
							? 'border-dark-600 text-primary-400 hover:bg-dark-700'
							: 'border-gray-300 text-primary-600 hover:bg-gray-50'
					}`}
					>
					{content.edit}
					</button>
					<a
					href={`${CONFIG.BASE_URL}/vcard/${uuid}`}
					download={`${v.name || 'contact'}.vcf`}
					className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border no-underline transition-colors ${
						isDark
							? 'border-dark-600 text-gray-300 hover:bg-dark-700'
							: 'border-gray-300 text-gray-700 hover:bg-gray-50'
					}`}
					>
					{content.download}
					</a>
					<button
					onClick={() => remove(uuid)}
					className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
					>
					{content.delete}
					</button>
					</div>
					</div>
				);
			})}
			</div>
		)}		</div>
		<VCardEditModal
		open={modal}
		onClose={() => setModal(false)}
		vcard={editingId ? { uuid: editingId, data: form } : null}
		onSaved={fetchVcards}
		/>
		</div>
	);
};

export default VCardManager;
